import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import useSEO from "@/lib/useSEO";
import TechSupportChat from "@/components/TechSupportChat";
import { ArrowRight, Wrench, Zap, Cpu, Settings, Cable, X } from "lucide-react";

const CATEGORY_ICON = {
  "Thermal Power Plant Services": Zap,
  "Industrial Systems": Cog,
  "Power Systems": Cable,
  "Mechanical Overhauling": Wrench,
  "C&I Services": Cpu,
};
function Cog(props) { return <Settings {...props} />; }

export default function Consultancy() {
  const [services, setServices] = useState([]);
  const [quoteOpen, setQuoteOpen] = useState(false);
  useSEO({ title: "Engineering Consultancy — Sarthi Associates", description: "Thermal Power, Industrial Systems, Power Systems, Mechanical Overhauling & C&I services. Book consultants or request a custom quote.", path: "/consultancy" });
  useEffect(() => { api.get("/consultancy/services").then((r) => setServices(r.data)); }, []);

  const byCategory = services.reduce((acc, s) => { (acc[s.category] = acc[s.category] || []).push(s); return acc; }, {});

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6" data-testid="consultancy-page">
      <div className="text-center">
        <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-sarthi-accent px-4 py-1 text-xs font-bold uppercase tracking-wider text-sarthi-primary">Engineering Consultancy</div>
        <h1 className="mt-3 font-display text-4xl font-extrabold text-sarthi-text sm:text-5xl">Expert consultants, on demand.</h1>
        <p className="mx-auto mt-3 max-w-2xl text-lg font-medium text-sarthi-muted">Thermal power, industrial systems, protection, mechanical overhauling and full C&amp;I services — book by the session or request a tailored quote.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button data-testid="consultancy-quote-btn" onClick={() => setQuoteOpen(true)} className="sarthi-btn-primary inline-flex items-center gap-2 px-5 py-3 text-white">Request Custom Quote <ArrowRight className="h-4 w-4" /></button>
        </div>
      </div>

      {Object.entries(byCategory).map(([cat, items]) => {
        const Icon = CATEGORY_ICON[cat] || Settings;
        return (
          <div key={cat} className="mt-10" data-testid={`consultancy-cat-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sarthi-secondary text-white"><Icon className="h-6 w-6" /></div>
              <h2 className="font-display text-2xl font-extrabold text-sarthi-text">{cat}</h2>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {items.map((s) => (
                <Link key={s.slug} to={`/consultancy/${s.slug}`} data-testid={`consultancy-card-${s.slug}`} className="sarthi-card sarthi-card-press flex flex-col p-5">
                  <div className="font-display text-lg font-extrabold leading-tight text-sarthi-text">{s.title}</div>
                  <div className="mt-2 flex-1 text-sm text-sarthi-muted">{s.short}</div>
                  <div className="mt-3 flex items-end justify-between">
                    <div><span className="font-display text-2xl font-extrabold text-sarthi-primary">₹{s.amount}</span><span className="ml-1 text-xs font-bold text-sarthi-muted">/ session</span></div>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-sarthi-primary">View <ArrowRight className="h-3 w-3" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        );
      })}

      {quoteOpen && <QuoteModal onClose={() => setQuoteOpen(false)} />}
      <TechSupportChat />
    </div>
  );
}

function QuoteModal({ onClose }) {
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", scope: "", budget: "", timeline: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    try { await api.post("/consultancy/quote", f); toast.success("Quote request received — we'll email you within 1 business day"); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Please fill all required fields"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="quote-modal">
      <div className="sarthi-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between"><h2 className="font-display text-xl font-extrabold">Request a Custom Quote</h2><button onClick={onClose}><X className="h-5 w-5" /></button></div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input data-testid="quote-name" placeholder="Your name" value={f.name} onChange={set("name")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="quote-company" placeholder="Company" value={f.company} onChange={set("company")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="quote-email" type="email" placeholder="Email" value={f.email} onChange={set("email")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="quote-phone" placeholder="Phone" value={f.phone} onChange={set("phone")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="quote-budget" placeholder="Budget (optional)" value={f.budget} onChange={set("budget")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="quote-timeline" placeholder="Timeline (optional)" value={f.timeline} onChange={set("timeline")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <textarea data-testid="quote-scope" placeholder="Describe the scope of work (min 10 chars)" value={f.scope} onChange={set("scope")} rows={5} className="rounded-xl border-2 border-sarthi-border px-3 py-2 sm:col-span-2" />
        </div>
        <button data-testid="quote-submit" onClick={submit} className="sarthi-btn-primary mt-4 w-full py-3 text-white">Send Quote Request</button>
      </div>
    </div>
  );
}
